/**
 * @file statementRunWork.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-20
 *
 * Words and small rules for a statement run that is handed over or shared
 * (Generate Statements → "Hand over…"). The server keeps the run's progress
 * (services/statementRunWorkService.js, rules in utils/statementRunWork.js);
 * this turns what it sends into what the screen says, and the screen's own
 * state into what it sends.
 *
 * 1.1.0 — the Accounts step: "Hand over to Accounts", the run's Accounts
 * confirmation ("Worldpay reconciled in Xero up to <period end> and the
 * Accounts flags reviewed" — same words as the server's
 * utils/statementRunWork.accountsConfirmationText) and why emailing customers
 * / printing wait for it.
 *
 * 1.2.0 — the confirmation is needed only on runs generated on or after
 * ACCOUNTS_STEP_FROM (19/10/2026); older runs go out without it, and the panel
 * says so (the tick can still be given).
 */

// The four-step layout (20 Oct 2026): Set up → Contents → Review → Send.
export const STEP_NAMES = {
  1: 'Set up',
  2: 'Contents',
  3: 'Review',
  4: 'Send'
};

// What each saved key is, in words — for "Jade changed where they go".
const KEY_WORDS = {
  step: 'the step',
  dates: 'the statement period',
  contacts: 'the contacts',
  excludedInvoices: 'which invoices are bundled',
  templateMode: 'the template',
  include: 'what is included',
  destination: 'where they go',
  suppressOverdueStamp: 'the OVERDUE stamp',
  includeAllUnpaidInvoices: 'the unpaid invoices option',
  officeEmail: 'the office email address',
  attachments: 'the extra PDFs',
  stage: 'whether it is with Accounts',
  accountsHandedTo: 'who it was handed to for Accounts',
  accountsConfirmed: 'the Accounts confirmation'
};

const DELIVERY_WORDS = {
  emailed_customer: 'Emailed to customers',
  emailed_office: 'Emailed to the office',
  printed: 'Printed',
  saved: 'Downloaded'
};

/** "Mon 12 Oct, 14:05" in London time, or '' when unknown. */
export function whenText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });
}

/**
 * The notice a colleague sees: "Cam Seymour handed over the September 2026
 * statements to you" — or, handed to them for Accounts' check, "Cam Seymour
 * handed the September 2026 statements to you for Accounts' check".
 */
export function handOverNoticeText(run) {
  const who = run?.addedByName || 'A colleague';
  const period = run?.period?.label || 'statement';
  if (run?.forAccounts) return `${who} handed the ${period} statements to you for Accounts' check`;
  return `${who} handed over the ${period} statements to you`;
}

/** Where a run has got to, in words: "Step 2 — Generate & approve". */
export function stageText(run) {
  const step = Number(run?.step) || (run?.isDraft ? 1 : null);
  if (!step) return run?.statements ? 'Generated' : 'Not started';
  return `Step ${step} — ${STEP_NAMES[step]}`;
}

/** "You, Jade Ward and Amy Hill" — the people on a run, the viewer first as "You". */
export function peopleText(people, meId) {
  const names = (people || [])
    .slice()
    .sort((a, b) => (a.userId === meId ? -1 : b.userId === meId ? 1 : 0))
    .map(p => (p.userId === meId ? 'You' : p.name));
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "Jade Ward changed where they go · Mon 12 Oct, 14:05", or null for one's own / none. */
export function lastChangeText(work, meId) {
  const c = work?.lastChange;
  if (!c || !c.by || c.by === meId) return null;
  const keys = (c.keys || []).filter(k => k !== 'step');
  const what = keys.length ? keys.map(k => KEY_WORDS[k] || k).join(', ') : 'the step';
  const when = whenText(c.at);
  return `${c.byName || 'A colleague'} changed ${what}${when ? ` · ${when}` : ''}`;
}

/** ["Emailed to customers: 120 (last by Jade Ward, Thu 8 Oct, 14:05)", …] */
export function deliveriesText(deliveries) {
  return (deliveries || []).map(d => {
    const label = DELIVERY_WORDS[d.method] || 'Sent';
    const when = whenText(d.lastAt);
    const by = d.lastByName ? `last by ${d.lastByName}${when ? `, ${when}` : ''}` : when;
    return `${label}: ${d.count}${by ? ` (${by})` : ''}`;
  });
}

/** The statement ids whose invoices are bundled: every statement except those left out. */
export function includedStatementIds(statements, excluded) {
  const out = new Set();
  for (const s of statements || []) if (!excluded?.has?.(s.id)) out.add(s.id);
  return out;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Dates the server will take: both set, the end not before the start. */
export function datesComplete(dates) {
  return !!dates && DATE_RE.test(dates.start || '') && DATE_RE.test(dates.end || '') && dates.end >= dates.start;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * The office address as stored on the run: null when it is the viewer's own
 * (whoever picks the run up sees their own), undefined when it is not yet an
 * address (still being typed — nothing is saved).
 */
export function officeEmailToStore(value, ownEmail) {
  const v = String(value || '').trim();
  if (!v || (ownEmail && v.toLowerCase() === String(ownEmail).toLowerCase())) return null;
  return EMAIL_RE.test(v) ? v : undefined;
}

/**
 * Why "Hand over…" cannot be used just now, or null when it can.
 * @param {object} p
 */
export function handOverBlock({ ready, busy, hasRun, onRun, contactsCount, datesOk }) {
  if (ready === false) return 'Handing over needs a database update that has not been run yet (statement_run_handover.sql).';
  if (busy) return 'Wait until the current send, print or generation has finished.';
  if (hasRun && onRun === false) return 'This run has been handed over — it is no longer yours to hand on.';
  if (!hasRun && !contactsCount) return 'Choose at least one contact first — there is nothing to hand over yet.';
  if (!hasRun && !datesOk) return 'Set the statement period first.';
  return null;
}

/** "Handed over to Jade Ward." plus what happened to the emails. */
export function handOverResultText(result) {
  const names = (result?.recipients || []).map(r => r.name);
  const to = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || 'your colleague');
  const parts = [result?.toAccounts
    ? (result?.keptOn
      ? `Handed to ${to} for Accounts' check — you are still on this run.`
      : `Handed to ${to} for Accounts' check. It is no longer on your screen.`)
    : (result?.keptOn ? `Shared with ${to} — you are still on this run.` : `Handed over to ${to}. It is no longer on your screen.`)];
  if ((result?.emailed || []).length) parts.push(`Emailed ${result.emailed.map(e => e.name).join(', ')}.`);
  for (const f of result?.emailFailed || []) parts.push(`The email to ${f.name} could not be sent (${f.error}) — they will still see it on Generate Statements.`);
  return parts.join(' ');
}

/** "October 2026" for a whole month, else "1 Oct – 15 Oct 2026" (as the server words it). */
export function periodLabel(dates) {
  if (!datesComplete(dates)) return 'statement';
  const sd = new Date(`${dates.start}T12:00:00Z`);
  const ed = new Date(`${dates.end}T12:00:00Z`);
  const lastDay = new Date(Date.UTC(sd.getUTCFullYear(), sd.getUTCMonth() + 1, 0)).getUTCDate();
  if (sd.getUTCDate() === 1 && ed.getUTCDate() === lastDay
      && sd.getUTCMonth() === ed.getUTCMonth() && sd.getUTCFullYear() === ed.getUTCFullYear()) {
    return sd.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }
  const short = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${short(sd)} – ${short(ed)} ${ed.getUTCFullYear()}`;
}

// ── The Accounts step ───────────────────────────────────────────────────────

/**
 * The first London day whose runs need the Accounts confirmation — the
 * server's utils/statementRunWork.ACCOUNTS_STEP_FROM (its test checks they
 * agree). The server decides from the run as stored and says so in
 * work.accounts.required; this is for the words.
 */
export const ACCOUNTS_STEP_FROM = '2026-10-19';

/** Does this run need the Accounts confirmation? The server's `required`; absent → yes. */
export function accountsRequired(accounts) {
  return accounts?.required !== false;
}

/** Why a run from before the Accounts step needs no confirmation (as the server words it). */
export function accountsNotRequiredText() {
  return `The Accounts confirmation isn't required for runs made before ${dmy(ACCOUNTS_STEP_FROM)} — `
    + 'these statements can be emailed to customers and printed without it (the tick can still be given).';
}

/** The message "Hand over to Accounts" starts with (the person may change it). */
export const ACCOUNTS_HANDOVER_MESSAGE = 'Statements approved — please check them before they go out.';

/** '2026-09-30' → '30/09/2026'. */
export function dmy(iso) {
  const s = String(iso || '').slice(0, 10);
  return DATE_RE.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '';
}

/** The words of the Accounts confirmation (as the server words it). */
export function accountsConfirmationText(periodEnd) {
  return `Worldpay reconciled in Xero up to ${dmy(periodEnd) || 'the period end'} and the Accounts flags reviewed`;
}

/** "Confirmed by Jade Ward, 19/10/2026 14:02", or null when not confirmed. */
export function accountsWhoText(accounts) {
  if (!accounts?.confirmed) return null;
  let when = '';
  const d = accounts.at ? new Date(accounts.at) : null;
  if (d && !Number.isNaN(d.getTime())) {
    when = d.toLocaleString('en-GB', {
      timeZone: 'Europe/London', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).replace(',', '');
  }
  return `Confirmed by ${accounts.byName || 'a colleague'}${when ? `, ${when}` : ''}`;
}

/** The banner on steps 2 and 3 while the run is with Accounts, or null. */
export function accountsBannerText(accounts) {
  if (accounts?.stage !== 'accounts') return null;
  if (accounts.confirmed) return 'With Accounts: confirmed below — the statements can now be emailed to customers or printed.';
  return accountsRequired(accounts)
    ? 'With Accounts: review the approvals and notes, check the flags, then confirm below.'
    : `With Accounts: review the approvals and notes, check the flags, then confirm below (not required for runs made before ${dmy(ACCOUNTS_STEP_FROM)}).`;
}

/**
 * Why the Accounts tick cannot be changed just now, or null when it can.
 * @param {object} p  { ready (server's migration), loaded (the run has been read), onRun, busy, isDraft }
 */
export function accountsToggleBlock({ ready, loaded, onRun, busy, isDraft }) {
  if (ready === false) return 'Not ready — migration statement_run_handover.sql has not run, so the Accounts confirmation cannot be kept.';
  if (!loaded) return 'Checking the run…';
  if (isDraft) return 'Generate the statements first.';
  if (onRun === false) return 'This run has been handed over — it is no longer yours to change.';
  if (busy) return 'Saving…';
  return null;
}

/**
 * Why a send to customers (email) or a print is greyed: the Accounts
 * confirmation for the run's period end is not in place. Null for the office
 * email and save / ZIP, which never wait for it, before the server's
 * migration (nothing is gated then), while the run is still loading, and for a
 * run generated before ACCOUNTS_STEP_FROM (the server's `required: false`).
 *
 * @param {object} p  { destination: 'customers'|'print'|'office'|'save', ready, loaded, accounts }
 */
export function accountsSendBlock({ destination, ready, loaded, accounts }) {
  if (destination !== 'customers' && destination !== 'print' && destination !== 'groups') return null;
  // Before the migration nothing is gated; while the run is still loading
  // the button is left alone (the server decides either way).
  if (ready === false || !loaded) return null;
  if (accounts?.confirmed) return null;
  // A run generated before the Accounts step existed goes out without it.
  if (!accountsRequired(accounts)) return null;
  const verb = destination === 'print' ? 'Printing' : destination === 'groups' ? 'Sending to customers' : 'Emailing customers';
  return `${verb} waits for Accounts to confirm "${accountsConfirmationText(accounts?.periodEnd)}" — tick it under Checks → Accounts.`;
}

/** Why "Hand over to Accounts" cannot be used just now, or null. */
export function accountsHandOverBlock({ handOverReason, statementsCount }) {
  if (handOverReason) return handOverReason;
  if (!statementsCount) return 'Generate the statements first — Accounts check the generated statements.';
  return null;
}
